import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Skeleton, Text } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';

import { assetLinkPath, isOwnLink, openInApp, trackingKindLabel, type LinkForContext } from './link-for';
import type { LandingLinksRead } from './useLandingLinks';
import * as S from './LandingPageComposer.styles';
import * as L from './LandingLinks.styles';

export interface LandingLinksProps {
  /** The landing page's spark id (its own link is left out of the list). */
  pageId: string;
  read: LandingLinksRead;
  /** Open an asset's composer (an in-app path). Without it the rows have no open action. */
  onOpen?: (path: string) => void;
  /** The asset the page was made for, when this session knows it (its row opens the exact composer it came from). */
  madeFor?: LinkForContext | null;
  /** The page's own tracking link panel (once the page is live), shown first so it can be copied from here. */
  ownLink?: React.ReactNode;
  onClose: () => void;
}

/** Placeholder rows while the first read is in flight. */
const SKELETON_ROWS = 2;

/**
 * "Links to this page", a sheet opened from the composer's header (a bottom sheet on a phone): the page's own tracking
 * link once it is live, then the ads' tracking links that send visitors to this landing page (an ad picks it as its
 * tracking link's destination), each a deep link to its asset. Their combined results live in the page's stats sheet
 * (the header's Stats pill, `AssetStatsSheet`).
 */
export const LandingLinks = ({
  pageId,
  read,
  onOpen,
  madeFor,
  ownLink,
  onClose,
}: LandingLinksProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle } = useSheetDismiss(onClose);
  const rows = React.useMemo(() => read.links.filter((link) => !isOwnLink(link, pageId)), [read.links, pageId]);

  const list = ((): React.ReactNode => {
    if (read.status === 'loading') {
      return (
        <L.LinkList aria-hidden="true">
          {Array.from({ length: SKELETON_ROWS }).map((_v, i) => (
            <L.LinkRow key={i}>
              <Skeleton block height={32} radius={8} />
            </L.LinkRow>
          ))}
        </L.LinkList>
      );
    }
    if (read.status === 'error') {
      return (
        <L.ErrorLine>
          <S.Hint text="The links to this page couldn't be loaded." />
          <Button
            variant="text"
            icon="refresh"
            label={t('Try again')}
            disabled={read.reading}
            onClick={() => {
              if (!read.reading) read.reload();
            }}
          />
        </L.ErrorLine>
      );
    }
    if (rows.length === 0) {
      return (
        <S.Hint text="No ad sends visitors here yet. Pick this page when you create or change an ad's tracking link." />
      );
    }
    return (
      <L.LinkList>
        {rows.map((link) => {
          const name = link.name || t('Untitled');
          // The same deep link as the made-for bar's.
          const path = assetLinkPath(link.kind, link.creative_id, madeFor);
          return (
            <L.LinkRow key={link.id}>
              <L.LinkCopy>
                <L.LinkKind raw text={trackingKindLabel(link.kind, t)} />
                {onOpen ? (
                  <L.LinkNameLink href={path} title={name} onClick={(e) => openInApp(e, path, onOpen)}>
                    <Text raw text={name} />
                  </L.LinkNameLink>
                ) : (
                  <L.LinkName raw text={name} title={name} />
                )}
              </L.LinkCopy>
              {onOpen ? (
                <L.LinkActions>
                  <Button variant="text" icon="arrow-right-circle" label={t('Open')} onClick={() => onOpen(path)} />
                </L.LinkActions>
              ) : null}
            </L.LinkRow>
          );
        })}
      </L.LinkList>
    );
  })();

  return (
    <Sheet title="Links to this page" onClose={onClose} closeHandle={closeHandle} maxWidth="720px">
      <L.SheetBody>
        {ownLink}
        <S.Section>
          <S.SectionTitle text="Ads sending visitors here" />
          <S.Hint text="The tracking links of your ads that send visitors to this page." />
          {list}
        </S.Section>
      </L.SheetBody>
    </Sheet>
  );
};

export default LandingLinks;
